/**
 * useBordereauDocuments — source de vérité des pièces du panel bordereau.
 *
 * **Pourquoi ce fichier existe** (2026-07-17). Le panel travaillait sur un
 * instantané figé : `openPanel(documents)` seede `pieceNumbers` +
 * `selectedDocuments[].piece_number` depuis les documents passés par l'appelant,
 * et **rien** ne les revalidait ensuite. Ces documents venaient d'un
 * `apiClient.get` ad-hoc posé dans le handler de clic de `NosPiecesSection`,
 * **hors TanStack Query, sans query key** : aucune invalidation de l'app ne
 * pouvait donc l'atteindre.
 *
 * Or les tools agents (`set_piece_numbers`, `rename_pieces`… — PR #573) écrivent
 * `case_documents.piece_number` côté serveur, et le front a déjà une boucle de
 * rafraîchissement pour ça (`case_documents_mutated` → invalidations dans
 * `WebSocketPoolProvider`). Elle atteignait `case-documents` / `folder-contents`
 * / `folder-tree` / `renumber-docs` — **jamais le panel**, faute de clé.
 *
 * Conséquence, si l'agent renumérotait pendant que le panel était ouvert :
 * `savePieceNumbers` réécrivait l'instantané périmé dans `bordereau_pieces`, puis
 * la génération le propageait sur `case_documents` **et**
 * `case_document_embeddings` (`propagatePieceNumbersToDocuments`) — le travail de
 * l'agent était annulé sans erreur ni log, et les citations RAG empoisonnées.
 *
 * Ce module donne une clé au fetch (`['bordereau-docs', caseId]`) pour que le
 * panel devienne atteignable comme n'importe quel autre consommateur.
 *
 * @see docs/features/renumber-pieces/plans/2026-07-17-numerotation-agentique.html §02
 */

import { useQuery, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import type { SelectedDocument } from '../types/bordereau.types';

export const BORDEREAU_DOCS_QUERY_KEY = 'bordereau-docs';

/** Clé de cache des pièces du panel. */
export function bordereauDocsQueryKey(caseId: string) {
  return [BORDEREAU_DOCS_QUERY_KEY, caseId] as const;
}

/**
 * Charge les pièces éligibles au bordereau : `nos_pieces`, dans toutes les
 * matières.
 *
 * Le périmètre ne dépend PAS du droit public. La fusion des pièces
 * « Décision(s) attaquée(s) » a existé ici jusqu'à la migration 142, qui a
 * dissous ce dossier obligatoire — décision produit : le bordereau ne propose
 * que Nos pièces, comme partout ailleurs, et l'avocat y déplace une décision
 * s'il veut la tamponner. Le mode Télérecours ne survit qu'à l'étape d'export
 * (libellés « productions »), pas dans le périmètre fetché.
 */
export async function loadBordereauDocuments(caseId: string): Promise<SelectedDocument[]> {
  const docs = (await apiClient.get<Record<string, unknown>[]>(
    `/cases/${caseId}/documents?category=nos_pieces&is_folder=false`,
  )) ?? [];

  return docs
    .filter((d) => d.is_folder !== true && d.document_type !== 'synthesis')
    .map((d) => ({
      id: d.id as string,
      name: d.name as string,
      size: (d.file_size as number) || 0,
      type: (d.document_type as string) || 'application/pdf',
      sourceDocumentType: typeof d.document_type === 'string' ? d.document_type : undefined,
      filePath: d.file_path as string | undefined,
      isFolder: d.is_folder === true,
      sourceIsFolder: typeof d.is_folder === 'boolean' ? d.is_folder : undefined,
      piece_number: typeof d.piece_number === 'string' ? d.piece_number : null,
      sourcePieceNumber: d.piece_number,
      documentCategory: typeof d.document_category === 'string' ? d.document_category : undefined,
      originalDocumentId: d.original_document_id === null
        ? null
        : typeof d.original_document_id === 'string'
          ? d.original_document_id
          : undefined,
      // Folder-based contract (2026-06-21) — propagation du chemin de dossier au
      // store pour qu'applyQuickNumbering puisse détecter les pièces du
      // sous-dossier « Décision(s) attaquée(s) » et les placer en tête
      // (R. 412-1 CJA). Source de vérité = présence physique dans le folder, pas
      // la subcategory tag.
      display_folder_path: (d.display_folder_path as string | null) ?? null,
    }));
}

/**
 * Charge les pièces **en peuplant le cache** sous `bordereauDocsQueryKey`.
 *
 * Utilisé par le handler de clic qui ouvre le panel : passer par `fetchQuery`
 * (plutôt qu'un `apiClient.get` nu) fait que la requête d'ouverture et la requête
 * de resynchronisation du panel partagent la même entrée de cache — le panel ne
 * refetche donc pas à l'ouverture, et l'invalidation agent atteint les deux.
 */
export function fetchBordereauDocuments(
  queryClient: QueryClient,
  caseId: string,
): Promise<SelectedDocument[]> {
  return queryClient.fetchQuery({
    queryKey: bordereauDocsQueryKey(caseId),
    queryFn: () => loadBordereauDocuments(caseId),
    staleTime: BORDEREAU_DOCS_STALE_TIME,
  });
}

/**
 * Relit les pièces en **court-circuitant le cache** (`staleTime: 0`), et met le
 * cache à jour au passage.
 *
 * Réservé à la réconciliation juste avant écriture : l'invalidation WS ne garantit
 * rien si l'avocat clique « Continuer » pendant que l'agent écrit encore. Le
 * refetch événementiel est un confort d'affichage ; celui-ci est une garde.
 */
export function refetchBordereauDocumentsNow(
  queryClient: QueryClient,
  caseId: string,
): Promise<SelectedDocument[]> {
  return queryClient.fetchQuery({
    queryKey: bordereauDocsQueryKey(caseId),
    queryFn: () => loadBordereauDocuments(caseId),
    staleTime: 0,
  });
}

/**
 * Fenêtre pendant laquelle l'entrée peuplée par le clic d'ouverture est réputée
 * fraîche — assez longue pour que le panel ne refetche pas immédiatement après
 * `openPanel`, assez courte pour qu'une réouverture ultérieure relise la DB.
 * L'invalidation agent (`case_documents_mutated`) prime de toute façon dessus.
 */
const BORDEREAU_DOCS_STALE_TIME = 30_000;

/**
 * Query live des pièces du dossier, montée par le panel tant qu'il est ouvert.
 *
 * C'est elle qui rend le panel **atteignable** : quand un tool agent mute les
 * pièces, `WebSocketPoolProvider` invalide `['bordereau-docs', caseId]`, cette
 * query refetche, et l'appelant reseede le store.
 */
export function useBordereauDocuments(caseId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: bordereauDocsQueryKey(caseId ?? ''),
    queryFn: () => loadBordereauDocuments(caseId as string),
    enabled: enabled && !!caseId,
    staleTime: BORDEREAU_DOCS_STALE_TIME,
  });
}
